import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { fetchCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const paymentMethod = searchParams.get('payment_method') || 'stripe';

  useEffect(() => {
    const pollStatus = async () => {
      if (!sessionId || attempts >= 10) {
        if (attempts >= 10) {
          setStatus('timeout');
        }
        return;
      }

      try {
        const endpoint = paymentMethod === 'paypal'
          ? `${API}/payments/paypal/status/${sessionId}`
          : `${API}/payments/stripe/status/${sessionId}`;

        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await axios.get(endpoint, {
          headers,
          withCredentials: true
        });

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          fetchCart(); // Refresh cart (should be empty now)
        } else {
          // Continue polling
          setAttempts(a => a + 1);
          setTimeout(pollStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking status:', error);
        setAttempts(a => a + 1);
        setTimeout(pollStatus, 2000);
      }
    };

    pollStatus();
  }, [sessionId, paymentMethod, token, attempts, fetchCart]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <h2 className="font-heading text-2xl font-semibold mb-2">Processing Payment</h2>
        <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
      </div>
    );
  }

  if (status === 'timeout') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md border-border text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-yellow-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold mb-2">Payment Processing</h2>
            <p className="text-muted-foreground mb-6">
              Your payment is being processed. You'll receive an email confirmation shortly.
            </p>
            <Button asChild className="w-full btn-lift">
              <Link to="/orders">View Orders</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4" data-testid="checkout-success">
      <Card className="w-full max-w-md border-border text-center">
        <CardContent className="pt-8 pb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="font-heading text-2xl font-semibold mb-2">Payment Successful!</h2>
          <p className="text-muted-foreground mb-6">
            Thank you for your purchase. Your books have been added to your library.
          </p>
          <div className="space-y-3">
            <Button asChild className="w-full btn-lift">
              <Link to="/library">Go to My Library</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/orders">View Order Details</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const CheckoutCancelPage = () => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md border-border text-center">
        <CardContent className="pt-8 pb-6">
          <h2 className="font-heading text-2xl font-semibold mb-2">Payment Cancelled</h2>
          <p className="text-muted-foreground mb-6">
            Your payment was cancelled. Your cart items are still saved.
          </p>
          <div className="space-y-3">
            <Button asChild className="w-full btn-lift">
              <Link to="/cart">Return to Cart</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/browse">Continue Shopping</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
